<template>
  <div class="group-container">
    <!-- Frame 3466235 - Main container -->
    <div class="group-main-container">
      <!-- Frame 3466251 - White card -->
      <div class="group-card">
        <!-- Title -->
        <h3 class="group-title">Administrador(es) del panel</h3>

        <!-- Frame 3466252 - Inputs container -->
        <div class="inputs-container">
          <!-- Scrollable inputs area -->
          <div class="inputs-scroll-area">
            <!-- Dynamic admin inputs -->
            <div v-for="(email, index) in adminEmails" :key="index" class="input-row">
              <CustomInput
                :id="`admin-name-label-${index + 1}`"
                v-model="adminNames[index]"
                :label="`Nombre del Administrador (${index + 1})`"
                placeholder="Ingresa el nombre"
                :full-width="true"
                :show-error="adminNames[index].trim() === ''"
                :error-message="'El nombre no puede estar vacío'"
                error-type="error"
              />
              <CustomInput
                :id="`admin-email-${index + 1}`"
                v-model="adminEmails[index]"
                :label="`Correo del Administrador (${index + 1})`"
                placeholder="userexample@email.com"
                :full-width="true"
                :show-error="!isEmailValid[index] && adminEmails[index].trim() !== ''"
                :error-message="getEmailErrorMessage()"
                error-type="error"
                @input="validateEmail(index)"
              />
              <div v-if="adminEmails.length > 1" class="delete-button" @click="removeAdmin(index)">
                <img :src="trashIcon" alt="Delete admin" width="24" height="24" />
              </div>
            </div>
          </div>
        </div>

        <!-- Add administrator button - Always at bottom -->
        <div class="add-admin-button-container">
          <button class="add-admin-icon-button" @click="addAdmin">
            <img :src="plusIcon" alt="Agregar administrador" width="16" height="16" />
          </button>
          <span class="add-admin-text">Agregar administrador</span>
        </div>
      </div>

      <!-- Save button removed - now handled by parent component -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, onMounted } from 'vue'
import CustomInput from './CustomInput.vue'
import plusIcon from '@/assets/plus-icon.svg'
import trashIcon from '@/assets/trash-icon-gray.svg'
import { DataValidatorService } from 'commons'
import { ApiService } from '@/services/apiService'
import { useBulkLoadStore } from '@/stores/bulkLoad'

const dataValidatorService = inject<DataValidatorService>('dataValidatorService')
const apiService = inject<ApiService>('apiService')
const bulkLoadStore = useBulkLoadStore()

if (!dataValidatorService) {
  throw new Error('dataValidatorService is not provided')
}
if (!apiService) {
  throw new Error('apiService is not provided')
}

const adminNames = ref([''])
const adminEmails = ref([''])
const isEmailValid = ref([true])
const isLoading = ref(false)
const businessId = computed(() => bulkLoadStore.selectedGroup?.id || '')

const validateEmail = (index: number) => {
  const email = adminEmails.value[index].trim()
  if (email === '') {
    isEmailValid.value[index] = false
    return
  }
  isEmailValid.value[index] = dataValidatorService.isValidEmail(email)
}

const getEmailErrorMessage = () => {
  return 'Correo electrónico inválido'
}

const loadExistingAdministrators = async () => {
  try {
    isLoading.value = true
    const existingAdmins = await apiService.getCompanyAdministrators(businessId.value)

    if (existingAdmins.emails && existingAdmins.emails.length > 0) {
      // Load existing emails
      adminNames.value = existingAdmins.emails.map((admin) => admin.name || '')
      adminEmails.value = existingAdmins.emails.map((admin) => admin.email)
      isEmailValid.value = new Array(adminEmails.value.length).fill(true)
    }
    bulkLoadStore.setCompanyAdministrators(existingAdmins)
  } catch (error) {
    console.error('Hubo un error: Error loading existing administrators ', error)
  } finally {
    isLoading.value = false
  }
}

const addAdmin = () => {
  // Add a new empty field for the next admin
  adminEmails.value.push('')
  isEmailValid.value.push(false)
  adminNames.value.push('')
}

const removeAdmin = (index: number) => {
  if (adminEmails.value.length > 1) {
    adminEmails.value.splice(index, 1)
    isEmailValid.value.splice(index, 1)
    adminNames.value.splice(index, 1)
  }
}

// handleSave function removed - now handled by parent component

// Expose functions and data for parent component to use
const refreshAdministrators = async () => {
  await loadExistingAdministrators()
}

// Expose functions to parent component
defineExpose({
  refreshAdministrators,
  adminEmails,
  isEmailValid,
  adminNames,
})

// Load existing administrators when component mounts
onMounted(() => {
  //loadExistingAdministrators() TODO: Uncomment this when the API is implemented
})
</script>

<style scoped>
/* Override group-card min-height for admins */
.group-card {
  min-height: 450px; /* Minimum height to accommodate 3 inputs + button */
}

/* Frame 3466252 - Inputs container */
.inputs-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 0px; /* Remove gap since button is now separate */
  width: 100%;
  height: auto;
  flex: 1; /* Allow it to grow */
  order: 1;
  align-self: stretch;
  flex-grow: 0;
}

/* Add admin button container - Always at bottom */
.add-admin-button-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 24px 0px 0px 0px; /* Add top padding for separation */
  width: 100%;
  height: auto;
  flex: none;
  order: 2;
  align-self: stretch;
  flex-grow: 0;
}

.add-admin-icon-button {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 4px;
  width: 60px;
  height: 40px;
  background: #9cd41c;
  border: none;
  border-radius: 30px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  flex: none;
  order: 0;
  flex-grow: 0;
  flex-shrink: 0;
}

.add-admin-icon-button:hover {
  background: #7bb516;
  transform: translateY(-1px);
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
}

.add-admin-icon-button:active {
  transform: translateY(0);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
}

.add-admin-text {
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #1a1a1a;
  white-space: nowrap;
}

/* Scrollable inputs area */
.inputs-scroll-area {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  width: 100%;
  max-height: 250px; /* Reduced height to leave space for button */
  overflow-y: auto;
  overflow-x: hidden; /* Remove horizontal scroll */
  flex: 1;
}

/* Custom scrollbar styling */
.inputs-scroll-area::-webkit-scrollbar {
  width: 6px;
}

.inputs-scroll-area::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.inputs-scroll-area::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.inputs-scroll-area::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Input row */
.input-row {
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  padding: 0px;
  gap: 16px;
  width: 100%;
  min-width: 0; /* Prevent overflow */
  height: 78px;
  flex: none;
  order: 0;
  align-self: stretch;
  flex-grow: 0;
}

.input-row:last-of-type {
  order: 1;
}

/* Delete button */
.delete-button {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 4px;
  flex: none;
  order: 1;
  flex-grow: 0;
}

.delete-button:hover {
  background-color: #f5f5f5;
}

/* Save button container is now handled by common CSS */

/* Override CustomInput styles to match Figma */
:deep(.input-field) {
  width: 100%;
  max-width: 772px;
  height: 78px;
  flex: 1;
  min-width: 0; /* Prevent overflow */
}

:deep(.input-text) {
  width: 100%;
  max-width: 772px;
  height: 48px;
  background: #ffffff;
  border: 1px solid #d5d5d5;
  border-radius: 16px;
  box-sizing: border-box;
}

/* Override AddButton styles */
:deep(.add-button) {
  width: 240px;
  height: 40px;
  background: #9cd41c;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 30px;
}

/* Override CustomButton styles for save button */
:deep(.custom-button.secondary) {
  width: 280px;
  height: 48px;
  background: #003896;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 30px;
}
</style>
